import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { PageLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Heart, ArrowRight, Spinner } from '@phosphor-icons/react';

const SignUp = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    age: '',
    gender: '',
    location: ''
  });

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!agreed) {
      toast.error('Please agree to the Terms of Service');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    const age = parseInt(formData.age);
    if (isNaN(age) || age < 18) {
      toast.error('You must be at least 21 years old');
      return;
    }

    setLoading(true);
    try {
      await register({
        email: formData.email,
        password: formData.password,
        name: formData.name,
        age: age,
        gender: formData.gender,
        location: formData.location
      });
      toast.success('Account created! Let\'s complete your application.');
      navigate('/application');
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout showFooter={false}>
      <div className="min-h-screen flex">
        {/* Left - Form */}
        <div className="flex-1 flex items-center justify-center p-8 md:p-12 rose-gradient">
          <div className="w-full max-w-md">
            <div className="mb-10 text-center">
              <div className="flex items-center justify-center gap-2 mb-6">
                <Heart weight="fill" className="w-8 h-8 text-primary" />
                <Heart weight="fill" className="w-5 h-5 text-amber-400" />
              </div>
              <h1 className="script-font text-3xl text-primary mb-2">Link, Laugh & Love</h1>
              <p className="font-serif text-2xl text-gray-800 mb-2">Start Your Journey</p>
              <p className="text-muted-foreground text-sm">
                Create your account to begin the matchmaking process
              </p>
            </div>

            {/* Resume-style form card */}
            <div className="resume-card rounded-lg p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="gold-accent"></div>
                <span className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                  Basic Information
                </span>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-1">
                  <Label htmlFor="name" className="form-label">Full Name</Label>
                  <Input
                    id="name"
                    name="name"
                    placeholder="Your full name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="signup-name-input"
                    className="form-input"
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="email" className="form-label">Email Address</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="signup-email-input"
                    className="form-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="age" className="form-label">Age</Label>
                    <Input
                      id="age"
                      name="age"
                      type="number"
                      min="18"
                      max="120"
                      placeholder="25"
                      value={formData.age}
                      onChange={handleChange}
                      required
                      data-testid="signup-age-input"
                      className="form-input"
                    />
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="gender" className="form-label">Gender</Label>
                    <Select 
                      value={formData.gender} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, gender: value }))}
                    >
                      <SelectTrigger data-testid="signup-gender-select" className="border-b-2 border-pink-200 rounded-none">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="non-binary">Non-binary</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="location" className="form-label">Location (City, State)</Label>
                  <Input
                    id="location"
                    name="location"
                    placeholder="Dallas, TX"
                    value={formData.location}
                    onChange={handleChange}
                    required
                    data-testid="signup-location-input"
                    className="form-input"
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="password" className="form-label">Password</Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    data-testid="signup-password-input"
                    className="form-input"
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="confirmPassword" className="form-label">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    data-testid="signup-confirm-password-input"
                    className="form-input"
                  />
                </div>

                <div className="flex items-start gap-3 pt-2">
                  <Checkbox 
                    id="terms" 
                    checked={agreed}
                    onCheckedChange={setAgreed}
                    data-testid="signup-terms-checkbox"
                    className="mt-1"
                  />
                  <Label htmlFor="terms" className="text-sm text-muted-foreground leading-relaxed cursor-pointer">
                    I agree to the Terms of Service and Privacy Policy
                  </Label>
                </div>

                <Button 
                  type="submit" 
                  disabled={loading}
                  className="btn-primary w-full py-6 mt-4"
                  data-testid="signup-submit-btn"
                >
                  {loading ? (
                    <Spinner className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Heart weight="fill" className="w-4 h-4 mr-2" />
                      Create Account
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            </div>

            <p className="text-center text-muted-foreground mt-8">
              Already have an account?{' '}
              <Link to="/login" className="text-primary font-medium hover:underline">
                Sign In
              </Link>
            </p>
          </div>
        </div>

        {/* Right - Decorative */}
        <div className="hidden lg:flex lg:w-1/2 navy-section items-center justify-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <Heart weight="fill" className="absolute top-20 right-20 w-64 h-64 text-primary" />
            <Heart weight="fill" className="absolute bottom-20 left-20 w-40 h-40 text-amber-400" />
          </div>
          <div className="relative z-10 text-center px-12">
            <Heart weight="fill" className="w-16 h-16 text-primary mx-auto mb-6" />
            <p className="script-font text-4xl text-white mb-4">Real Love Starts</p>
            <p className="script-font text-4xl text-pink-200 mb-8">with a Real Connection</p>
            <div className="gold-accent mx-auto mb-8"></div>
            <p className="text-pink-100/70 text-sm leading-relaxed max-w-sm mx-auto">
              Join our community of intentional singles looking for meaningful, 
              faith-centered connections.
            </p>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default SignUp;
